import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Row, Col, Form, Badge } from 'react-bootstrap';
import apiDefinitions from '../../core/api/apiDefinitions';
import { API_BASE_URL } from '../../core/api/api';
import { showError, showSuccessAlert } from '../../core/alert/sweetAlertConfig';
import CollapseHeader from '../../core/common/collapse-header';

interface Gate {
    id: number;
    name: string;
    location?: { id: number; name: string };
    relayDevice?: { id: number; name: string };
    relayChannelNumber: number;
    status: string;
}

interface EwelinkToken {
    expireAt: string;
    region: string;
    apiDomain?: string;
}

const GateController: React.FC = () => {
    const [gates, setGates] = useState<Gate[]>([]);
    const [selectedGate, setSelectedGate] = useState<Gate | null>(null);
    const [tokenInfo, setTokenInfo] = useState<EwelinkToken | null>(null);
    const [pulseDuration, setPulseDuration] = useState(10);
    const [loading, setLoading] = useState(false);
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [activityLog, setActivityLog] = useState<{ action: string; time: string; type: string }[]>([]);
    const timeoutRef = useRef<any>(null);

    useEffect(() => {
        fetchGates();
        fetchTokenStatus();
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const fetchGates = async () => {
        try {
            const response = await apiDefinitions.getGates(0, 100, {});
            if (response?.data?.status === 'S1000') {
                const data = response.data.data || [];
                setGates(data);
                if (data.length > 0 && !selectedGate) setSelectedGate(data[0]);
            }
        } catch (err) {
            console.error('Error fetching gates:', err);
        }
    };

    const fetchTokenStatus = async () => {
        try {
            const response = await (apiDefinitions as any).getEwelinkToken();
            if (response?.data?.status === 'S1000') {
                setTokenInfo(response.data.data);
            } else {
                setTokenInfo(null);
            }
        } catch (err) {
            setTokenInfo(null);
            console.error('Error fetching token:', err);
        }
    };

    const addLog = (action: string, type: string) => {
        setActivityLog(prev => [{ action, time: new Date().toLocaleTimeString(), type }, ...prev].slice(0, 10));
    };

    const handleAction = async (type: 'open' | 'on' | 'off') => {
        if (!selectedGate) return;
        setLoading(true);
        try {
            let response;
            if (type === 'open') {
                response = await (apiDefinitions as any).openGate(selectedGate.id, pulseDuration);
            } else if (type === 'on') {
                response = await (apiDefinitions as any).turnOnGate(selectedGate.id);
            } else {
                response = await (apiDefinitions as any).turnOffGate(selectedGate.id);
            }

            if (response?.data?.status === 'S1000') {
                showSuccessAlert(response.data.statusDescription || `${selectedGate.name} triggered successfully`);
                const actionVerb = type === 'open' ? `Pulsed ( ${pulseDuration}s )` : type === 'on' ? 'Locked Open' : 'Closed';
                addLog(`${selectedGate.name}: ${actionVerb}`, 'Success');

                // Update UI lock state
                if (type === 'on') {
                    setIsUnlocked(true);
                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                } else if (type === 'off') {
                    setIsUnlocked(false);
                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                } else if (type === 'open') {
                    setIsUnlocked(true);
                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                    timeoutRef.current = setTimeout(() => {
                        setIsUnlocked(false);
                        timeoutRef.current = null;
                    }, pulseDuration * 1000);
                }
            } else {
                showError(response?.data?.statusDescription || 'Operation failed');
                addLog(`Error: ${selectedGate.name} ${type}`, 'Error');
            }
        } catch (err) {
            showError('Network error connecting to gate controller');
            addLog(`Network Error: ${selectedGate.name}`, 'Error');
        } finally {
            setLoading(false);
        }
    };

    const handleLink = () => {
        window.open(`${API_BASE_URL}/gatecontrol/ewelink/oauth/start`, '_blank');
    };

    const isTokenValid = tokenInfo ? new Date(tokenInfo.expireAt).getTime() > Date.now() : false;

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="row">
                    <div className="col-md-12">
                        {/* Standard Page Header with Sidebar Compatibility */}
                        <div className="page-header">
                            <div className="row align-items-center">
                                <div className="col">
                                    <h4 className="page-title">Gate Controller Dashboard</h4>
                                </div>
                                <div className="col-auto text-end">
                                    <div className="head-icons">
                                        <CollapseHeader />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Connection Card Section */}
                        <Row className="mb-4">
                            <Col md={12}>
                                <Card className="border-0 shadow-sm">
                                    <Card.Body className="p-4">
                                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                            <div className="flex-grow-1">
                                                <h6 className="text-muted small fw-bold text-uppercase mb-3">Ewelink OAuth Token</h6>
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className={`rounded-circle me-2 d-flex align-items-center justify-content-center ${isTokenValid ? 'bg-success text-white' : 'bg-danger text-white'}`} style={{ width: '20px', height: '20px' }}>
                                                        <div className="bg-white rounded-circle animate-pulse" style={{ width: '8px', height: '8px' }}></div>
                                                    </div>
                                                    <span className={`fw-bold ${isTokenValid ? 'text-success' : 'text-danger'}`}>
                                                        {isTokenValid ? 'Token active' : (tokenInfo ? 'Token expired' : 'Link Required')}
                                                    </span>
                                                </div>
                                                {tokenInfo ? (
                                                    <div className="text-secondary small mt-3">
                                                        <div className="mb-1"><span className="text-muted fw-bold me-2">Expires at:</span> {tokenInfo.expireAt}</div>
                                                        <div><span className="text-muted fw-bold me-2">Region:</span> {tokenInfo.region} • {tokenInfo.apiDomain || 'N/A'}</div>
                                                        {!isTokenValid && (
                                                            <div className="text-danger fw-bold small mt-2">
                                                                <i className="ti ti-alert-circle me-1"></i>
                                                                Your token has expired. Please click "Link eWeLink" to renew your connection.
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <p className="text-muted small mb-0 mt-3">Please link your eWeLink account to enable operations for all gates.</p>
                                                )}
                                            </div>
                                            <div className="d-flex gap-2 align-items-center">
                                                <Button variant="success" size="sm" onClick={handleLink} className="fw-bold px-4 rounded-pill shadow-sm">
                                                    <i className="ti ti-link me-1"></i> Link eWeLink
                                                </Button>
                                                <Button variant="outline-secondary" size="sm" onClick={fetchTokenStatus} className="rounded-pill px-3 shadow-sm">
                                                    <i className="ti ti-refresh me-1"></i> Refresh
                                                </Button>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Main Operational Area */}
                        <Row>
                            {/* Gate Selector */}
                            <Col md={12} className="mb-4">
                                <Card className="border-0 shadow-sm">
                                    <Card.Body className="p-3 text-center">
                                        <div className="d-flex align-items-center justify-content-center flex-wrap gap-2">
                                            {gates.map(gate => (
                                                <Button 
                                                    key={gate.id}
                                                    variant={selectedGate?.id === gate.id ? "primary" : "outline-secondary"}
                                                    onClick={() => setSelectedGate(gate)}
                                                    size="sm"
                                                    className={`px-4 rounded-pill d-flex align-items-center ${selectedGate?.id === gate.id ? 'shadow-sm active' : ''}`}
                                                >
                                                    <i className={`ti ti-door${selectedGate?.id === gate.id ? '-open' : ''} me-2`}></i>
                                                    {gate.name}
                                                </Button>
                                            ))}
                                            {gates.length === 0 && <span className="text-muted small">No gates found. configure in Administration.</span>}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Controls */}
                            <Col lg={7} xl={8}>
                                <Card className="border-0 shadow-sm mb-4" style={{ minHeight: '400px' }}>
                                    <Card.Header className="bg-transparent border-bottom-0 pt-4 px-4">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0 fw-bold">
                                                <i className="ti ti-settings-automation me-2 text-primary"></i>
                                                Control: {selectedGate?.name || '---'}
                                            </h5>
                                            {selectedGate && (
                                                <Badge bg={selectedGate.status === 'ACTIVE' ? 'success' : 'danger'} className="px-3 py-2">
                                                    {selectedGate.status}
                                                </Badge>
                                            )}
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        {selectedGate ? (
                                            <Row className="align-items-center h-100">
                                                <Col md={5} className="text-center border-md-end mb-4 mb-md-0">
                                                    <div className="py-4">
                                                        <i className={`ti ti-lock${isUnlocked ? '-open text-success' : ' text-danger'}`} style={{ fontSize: '100px', transition: 'all 0.3s ease' }}></i>
                                                        <div className="mt-3">
                                                            <div className="fw-bold text-dark">{selectedGate.name}</div>
                                                            <div className="text-muted small">{selectedGate.relayDevice?.name || 'Relay Device'} (CH {selectedGate.relayChannelNumber})</div>
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col md={7} className="ps-md-5">
                                                    <div className="p-4 rounded-3 mb-4 shadow-sm" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <div className="fw-bold fs-6"><i className="ti ti-clock-play me-2 text-success"></i>Pulse Duration</div>
                                                            <div className="d-flex align-items-center bg-white border rounded px-2 shadow-sm" style={{ width: '130px' }}>
                                                                <Form.Control 
                                                                    type="number" 
                                                                    min={5} 
                                                                    max={60}
                                                                    className="border-0 text-center fw-bold" 
                                                                    style={{ boxShadow: 'none' }}
                                                                    value={pulseDuration} 
                                                                    onChange={(e) => setPulseDuration(Math.max(1, parseInt(e.target.value) || 1))}
                                                                />
                                                                <span className="text-muted small ms-1">sec</span>
                                                            </div>
                                                        </div>
                                                        <Button 
                                                            variant="success" 
                                                            className="w-100 py-3 fw-bold fs-5 shadow-sm"
                                                            onClick={() => handleAction('open')}
                                                            disabled={loading || !isTokenValid}
                                                        >
                                                            {loading ? <i className="ti ti-loader fa-spin me-2"></i> : <i className="ti ti-door-open me-2"></i>}
                                                            PULSE OPEN
                                                        </Button>
                                                    </div>

                                                    <div className="row g-2">
                                                        <div className="col-6">
                                                            <Button variant="outline-primary" className="w-100 py-2 fw-bold shadow-sm" onClick={() => handleAction('on')} disabled={loading || !isTokenValid}>
                                                                KEEP OPEN
                                                            </Button>
                                                        </div>
                                                        <div className="col-6">
                                                            <Button variant="outline-danger" className="w-100 py-2 fw-bold shadow-sm" onClick={() => handleAction('off')} disabled={loading || !isTokenValid}>
                                                                CLOSE GATE
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        ) : (
                                            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center py-5">
                                                <div className="bg-light rounded-circle p-4 mb-3">
                                                    <i className="ti ti-mouse-pointer fs-1 text-muted"></i>
                                                </div>
                                                <h6 className="text-muted">Select a gate from the list above to activate controls</h6>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Activity Log */}
                            <Col lg={5} xl={4}>
                                <Card className="border-0 shadow-sm h-100">
                                    <Card.Header className="bg-transparent border-bottom pt-4 px-4 pb-3">
                                        <h6 className="mb-0 fw-bold">
                                            <i className="ti ti-history me-2 text-secondary"></i>
                                            Recent Actions
                                        </h6>
                                    </Card.Header>
                                    <Card.Body className="p-0 text-center">
                                        <div className="table-responsive" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                                            <table className="table table-hover table-nowrap mb-0 border-0">
                                                <thead className="thead-light sticky-top">
                                                    <tr>
                                                        <th className="small py-2 ps-4 text-start">Event</th>
                                                        <th className="small py-2 text-end pe-4">Time</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {activityLog.map((log, i) => (
                                                        <tr key={i}>
                                                            <td className="small py-3 ps-4 border-bottom text-start">
                                                                <div className="d-flex align-items-center">
                                                                    <div className={`rounded-circle me-2 ${log.type === 'Success' ? 'bg-success' : 'bg-danger'}`} style={{ width: '6px', height: '6px' }}></div>
                                                                    {log.action}
                                                                </div>
                                                            </td>
                                                            <td className="small py-3 text-end text-muted pe-4 border-bottom">{log.time}</td>
                                                        </tr>
                                                    ))}
                                                    {activityLog.length === 0 && (
                                                        <tr>
                                                            <td colSpan={2} className="text-center py-5 text-muted small">
                                                                <i className="ti ti-clipboard-list d-block fs-3 mb-2 opacity-25"></i>
                                                                No control actions recorded
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                </div>
            </div>
            <style>{`
                .animate-pulse {
                    animation: pulse 2s infinite;
                }
                @keyframes pulse {
                    0% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.5; transform: scale(0.85); }
                    100% { opacity: 1; transform: scale(1); }
                }
                .border-md-end {
                    border-right: 1px solid #dee2e6;
                }
                @media (max-width: 767.98px) {
                    .border-md-end {
                        border-right: none;
                        border-bottom: 1px solid #dee2e6;
                    }
                }
            `}</style>
        </div>
    );
};

export default GateController;
